<template>
  <div
    class="tw-rounded-2xl tw-bg-white dark:tw-bg-slate-800 tw-border tw-border-slate-100 dark:tw-border-slate-700 tw-shadow tw-py-3.5 tw-flex tw-flex-col tw-gap-3"
  >
    <div
      class="tw-text-sm tw-text-slate-700 dark:tw-text-slate-50 tw-font-geist tw-font-medium tw-px-4"
    >
      <div class="tw-flex tw-justify-between tw-items-center tw-gap-3">
        <div class="tw-w-full tw-max-w-52 tw-flex tw-gap-2 tw-items-center">
          <i class="pi pi-search tw-text-sm tw-text-slate-500" />
          <InputText
            class="tw-bg-none tw-bg-transparent tw-h-7 tw-w-full tw-border-none tw-outline-none focus:tw-border-none focus:tw-outline-none focus:tw-shadow-none tw-p-0 tw-text-sm tw-font-medium tw-font-geist"
            :placeholder="`Search ${
              props.selectedShow === 'all' ? 'Shows' : 'Episodes'
            }`"
            v-model="tableFilters.global.value"
            :disabled="isTableEmpty"
          />
        </div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <Button
            icon="pi pi-sync tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm"
            class="tw-h-7 tw-shadow tw-w-8 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-sm tw-font-medium tw-font-geist"
            @click="refreshData()"
          />
          <download-csv :data="formattedData">
            <Button
              label="CSV"
              icon="pi pi-download tw-mr-1.5 tw-text-sm"
              class="tw-h-7 tw-shadow tw-w-[68px] tw-px-2 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm tw-font-medium tw-font-geist"
              :disabled="isTableEmpty"
            ></Button>
          </download-csv>
        </div>
      </div>
    </div>
    <div>
      <DataTable
        :value="formattedData"
        :globalFilterFields="['show_id', 'episode_id', 'title']"
        :filters="tableFilters"
        :paginator="true"
        :rows="10"
        responsiveLayout="scroll"
        :loading="props.loading"
        class="tw-bg-none tw-bg-transparent podcaster-shows-table"
        table-class="tw-bg-none tw-bg-transparent"
        :row-class="() => 'tw-bg-none tw-bg-transparent'"
      >
        <Column field="title" header="Title" />
        <Column v-if="props.selectedShow === 'all'" field="is_ai" header="AI" />
        <Column
          v-if="props.selectedShow === 'all'"
          field="is_active"
          header="Active"
        />
        <Column field="views" header="Reach" />
        <Column field="plays" header="Sessions" />
        <Column field="listeners" header="Unique Listeners" />
        <Column field="ctr" header="CTR" />

        <template #empty>
          <div
            class="tw-flex tw-items-center tw-justify-center tw-min-h-[360px] tw-w-full tw-text-sm tw-text-slate-500 dark:tw-text-slate-300 tw-font-medium tw-font-geist"
          >
            <LoaderSm v-if="props.loading" />
            <span v-else>Show data populating...</span>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { computed, ref } from "vue";
import { FilterMatchMode } from "primevue/api";
import LoaderSm from "@/components/LoaderSm.vue";

const props = defineProps(["loading", "selectedShow", "data"]);

const emit = defineEmits(["refreshData"]);

const tableFilters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const isTableEmpty = computed(
  () => !Array.isArray(props.data) || props.data.length === 0
);

const formattedData = computed(() => {
  return props.data.map((item) => {
    const formatted = {
      ...item,
      views: formatNumber(item.views),
      plays: formatNumber(item.plays),
      listeners: formatNumber(item.listeners),
      ctr: formatCTR(item.ctr),
    };

    if (props.selectedShow === "all") {
      formatted.is_ai = formatYesNo(item.is_ai);
      formatted.is_active = formatYesNo(item.is_active);
    }

    return formatted;
  });
});

const formatNumber = (num) => {
  return new Intl.NumberFormat("en-US").format(num);
};

const formatYesNo = (value) =>
  value === true ? "Yes" : value === false ? "No" : "";

const formatCTR = (ctr) => {
  return Number(ctr).toFixed(3);
};

const refreshData = () => {
  // Implement refresh functionality here
  emit("refreshData");
};
</script>

<style>
.podcaster-shows-table .p-paginator-bottom {
  border-color: transparent !important;
}
.podcaster-shows-table .p-datatable-thead th {
  background-color: transparent !important;
}
.podcaster-shows-table .p-paginator {
  background-color: transparent !important;
  border-color: transparent !important;
}
.podcaster-shows-table .p-datatable,
.podcaster-shows-table .p-datatable-wrapper,
.podcaster-shows-table .p-datatable-tbody > tr,
.podcaster-shows-table .p-datatable-tbody > tr > td {
  background-color: transparent !important;
}
.podcaster-shows-table td,
.podcaster-shows-table th {
  border-color: #f1f5f9 !important; /* tw-border-slate-100 */
}
.tw-dark .podcaster-shows-table td,
.tw-dark .podcaster-shows-table th {
  border-color: #334155 !important; /* dark:tw-border-slate-700 */
}

.podcaster-shows-table .p-datatable-wrapper {
  min-height: 349px;
}
</style>
